import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { fukttestFaq, fukttestSections } from "@/data/nationella-guider";
import { AdSlot } from "@/components/AdSlot";
import { DeferredIsland } from "@/components/DeferredIsland";


const URL = "https://www.akhusbilfritid.se/fukttest-husbil";
const loadDampScanner = () =>
  import("@/components/DampAiScanner").then((module) => ({ default: module.DampAiScanner }));
const loadMoistureLab = () =>
  import("@/components/MoistureLab").then((module) => ({ default: module.MoistureLab }));

export const Route = createFileRoute("/fukttest-husbil")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(fukttestFaq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Fukttest husbil & husvagn – gränsvärden och kostnad" },
      {
        name: "description",
        content:
          "Fukttest av husbil och husvagn: vad gränsvärdena betyder, våra tolv mätpunkter och vad en fuktskada kostar att laga i varje stadium.",
      },
      {
        name: "keywords",
        content:
          "fukttest husbil, fukttest husvagn, fuktskada husvagn, fuktmätning husbil, fuktvärde husbil, fuktprotokoll begagnad husbil",
      },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "Fukttest av husbil & husvagn – gränsvärden, kostnad och egenkontroll" },
      {
        property: "og:description",
        content:
          "Vad fuktvärdena betyder, var skadorna börjar och vad de kostar i varje stadium. Från tolv mätpunkter i vår verkstad i Kalix.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${URL}#article`,
          headline: "Fukttest av husbil och husvagn – gränsvärden, kostnader och egenkontroll",
          url: URL,
          inLanguage: "sv-SE",
          about: "Fuktmätning i husbil och husvagn",
          author: { "@type": "Organization", name: "AK Husbil & Fritid" },
          publisher: { "@type": "Organization", name: "AK Husbil & Fritid" },
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Guider", item: "https://www.akhusbilfritid.se/guider" },
            { "@type": "ListItem", position: 3, name: "Fukttest", item: URL },
          ],
        }),
      },
    ],
  }),
  component: FukttestPage,
});

const levels = [
  { v: "< 15 %", h: "Torrt", p: "Normalvärde. Ingen åtgärd, mät igen nästa höst." },
  { v: "15–20 %", h: "Förhöjt", p: "Följ upp och täta i tid. 1 500–4 000 kr." },
  { v: "20–25 %", h: "Inträngning", p: "Väggsektion öppnas och torkas. 8 000–20 000 kr." },
  { v: "> 25 %", h: "Blött", p: "Röta trolig, byte av vägg eller golv. 25 000–70 000 kr." },
];

function FukttestPage() {
  return (
    <>
      <PageHeader
        label="Nationell guide · Fukt"
        title={
          <>
            Fukttest — <span className="font-italic-display text-accent">tolv</span> punkter, inga genvägar.
          </>
        }
        sub="Fukt är den enskilt största ekonomiska risken i en husbil eller husvagn. Skillnaden mellan att hitta 17 % och 26 % är ofta två vintrar — och tiotusentals kronor. Så här mäter vi, och så gör du din egen kontroll."
      />

      <section className="container-edge pt-10">
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-accent/30 bg-warm p-6 md:p-7">
          <div>
            <p className="eyebrow">Verktyg</p>
            <h2 className="mt-1 font-display text-xl md:text-2xl">Fyll i dina egna värden — få besked direkt</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Vår protokollmall med alla tolv mätpunkter, färgkodning och utskrift.
            </p>
          </div>
          <Link
            to="/verktyg/fuktprotokoll"
            className="inline-flex items-center rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background"
          >
            Öppna fuktprotokollet
          </Link>
        </div>
      </section>

      {/* Annonsyta innan skannern — läsaren passerar den på väg mot verktyget. */}
      <div className="container-edge pt-10">
        <AdSlot variant="in-article" label="Annons" className="my-0" />
      </div>

      <section className="container-edge pt-14 md:pt-20">
        <div className="mb-8 rounded-2xl border border-accent/30 bg-accent/5 p-6">
          <p className="eyebrow">Snabbt svar</p>
          <p className="mt-2 text-base leading-relaxed">
            Ett verkstadspris ligger normalt från cirka 1 500 kr för en tidig tätning till 70 000 kr eller mer vid röta.
            Som arbetsgräns använder vi under 15 % som torrt, 15–20 % som förhöjt och över 20 % som skäl för fortsatt kontroll.
            En kamera kan hitta visuella risktecken, men bara en riktig fuktmätare kan fastställa ett mätvärde.
          </p>
        </div>
        <p className="eyebrow">Gränsvärden och vad de kostar</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {levels.map((l) => (
            <div key={l.v} className="rounded-3xl border border-border bg-card p-6">
              <p className="font-display text-3xl leading-none text-accent">{l.v}</p>
              <p className="mt-3 font-display text-lg leading-tight">{l.h}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{l.p}</p>
            </div>
          ))}
        </div>
        <p className="mt-5 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Varje fordon vi säljer mäts i alla tolv punkter före leverans, och
          värdena följer med annonsen. Du ser hela lagret med bilder och
          specifikationer i vår butik på{" "}
          <a
            href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
          >
            Fyndanu.se
          </a>
          .
        </p>
      </section>

      {/* Skannern ligger direkt efter kostnadsrutorna: där är oron störst och
          viljan att kontrollera sin egen bil som högst. */}
      <section id="fuktskanner" className="container-edge pt-10 scroll-mt-24">
        <p className="mb-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Vet du var din egen bil ligger? Fota området du misstänker — du får en
          bedömning på under en minut.
        </p>
        <DeferredIsland
          load={loadDampScanner}
          minHeight={560}
          rootMargin="700px"
          label="Förbereder fuktskannern…"
        />
        <div className="mt-6">
          <DeferredIsland
            load={loadMoistureLab}
            minHeight={260}
            rootMargin="500px"
            label="Förbereder fördjupad kontroll…"
          />
        </div>
      </section>


      <div className="pt-14 md:pt-20">
        <HubEditorial eyebrow="Guiden" sections={fukttestSections} faq={fukttestFaq} />
      </div>

      {/* Andra annonsytan: efter guidetexten, innan lästipsen. */}
      <div className="container-edge pt-12">
        <AdSlot variant="in-article-2" label="Annons" className="my-0" />
      </div>

      <section className="container-edge pb-20 md:pb-24">
        <div className="rounded-3xl border border-border bg-warm p-7 md:p-10">
          <p className="eyebrow">Läs vidare</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <Link
              to="/tester"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Långtidstester — samma bilar mätta år efter år</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/solceller-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Solceller — så monteras panelen utan fuktrisk</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/fordon"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Fordon i lager — fukttestade vid leverans</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
